import { useState } from 'react'
import { FlaskConical } from 'lucide-react'
import PageHeader from '../components/layout/PageHeader.jsx'
import Button from '../components/ui/Button.jsx'
import Card from '../components/ui/Card.jsx'
import ContextStep from '../features/guidedObservation/components/ContextStep.jsx'
import EvidenceLedger from '../features/guidedObservation/components/EvidenceLedger.jsx'
import ExperienceStep from '../features/guidedObservation/components/ExperienceStep.jsx'
import GlossaryDialog from '../features/guidedObservation/components/GlossaryDialog.jsx'
import QuestionStep from '../features/guidedObservation/components/QuestionStep.jsx'
import SummaryStep from '../features/guidedObservation/components/SummaryStep.jsx'
import { demoGlossary, demoQuestions } from '../features/guidedObservation/demo/questions.js'
import { demoProfiles } from '../features/guidedObservation/demo/profiles.js'
import { evaluateObservation, findConflictingObservationIds, getCompatibleProfiles, scoreProfiles, selectNextQuestion } from '../features/guidedObservation/engine/engine.js'
import useGuidedObservation from '../features/guidedObservation/hooks/useGuidedObservation.js'

export default function GuidedObservationPage() {
  const [state, dispatch] = useGuidedObservation()
  const [showGlossary, setShowGlossary] = useState(false)
  const currentQuestion = demoQuestions.find((question) => question.id === state.currentQuestionId)
  const compatible = getCompatibleProfiles(demoProfiles, state.observations)
  const scored = scoreProfiles(demoProfiles, state.observations)
  const conflicts = findConflictingObservationIds(demoProfiles, state.observations)
  const findNext = (observations) => selectNextQuestion({ questions: demoQuestions, profiles: demoProfiles, observations, context: state.context, experience: state.experience })
  const edit = (questionId) => dispatch({ type: 'EDIT', questionId })

  const start = () => {
    const next = findNext(state.observations)
    dispatch({ type: 'SET_CONTEXT', value: state.context, questionId: next?.question.id || null })
  }

  const answer = (observation) => {
    const base = { ...state.observations }
    delete base[currentQuestion.id]
    const result = evaluateObservation({ profiles: demoProfiles, observations: base, questionId: currentQuestion.id, observation })
    const next = findNext(result.nextObservations)
    dispatch({ type: 'RECORD', questionId: currentQuestion.id, observations: result.nextObservations, nextQuestionId: next?.question.id || null, message: result.message })
  }

  const goBack = () => {
    const prior = [...state.history].reverse().find((id) => id !== state.currentQuestionId)
    dispatch(prior ? { type: 'BACK', questionId: prior } : { type: 'BACK', stage: 'context' })
  }

  const continueObservation = () => {
    const next = findNext(state.observations)
    if (next) dispatch({ type: 'CONTINUE', questionId: next.question.id })
  }

  const copyData = async () => {
    const payload = { recordType: 'guided-observation-prototype', version: '0.1', experience: state.experience, context: state.context, observations: state.observations, feedback: state.feedback }
    try { await navigator.clipboard.writeText(JSON.stringify(payload, null, 2)) } catch { /* Clipboard may be restricted. */ }
  }

  return <>
    <PageHeader
      eyebrow="Testing · Prototype"
      title="Guided Observation Prototype"
      description="A controlled interaction prototype for recording field observations—not a plant identification tool."
      actions={<Button variant="secondary" onClick={() => setShowGlossary(true)}>Glossary</Button>}
      metadata={<span className="guided-prototype-badge"><FlaskConical aria-hidden="true" /> Synthetic data · not identification</span>}
    />
    <div className="guided-boundary" role="note"><strong>No identification claim.</strong> This isolated prototype uses artificial anonymous profiles, original schematics and local browser storage. It does not query the botanical knowledge base.</div>
    {state.stage === 'experience' && <ExperienceStep value={state.experience} onChange={(value) => dispatch({ type: 'SET_EXPERIENCE', value })} onContinue={() => dispatch({ type: 'SET_EXPERIENCE', value: state.experience, advance: true })} />}
    {state.stage === 'context' && <ContextStep value={state.context} onChange={(value) => dispatch({ type: 'UPDATE_CONTEXT', value })} onContinue={start} onBack={() => dispatch({ type: 'BACK', stage: 'experience' })} />}
    {state.stage === 'question' && currentQuestion && <>
      <QuestionStep
        question={currentQuestion}
        reason={`${currentQuestion.why} ${compatible.length || demoProfiles.length} anonymous profiles are currently in this limited demonstration.`}
        experience={state.experience}
        existing={state.observations[currentQuestion.id]}
        progress={{ answered: Object.keys(state.observations).length, total: demoQuestions.length }}
        message={state.lastMessage}
        onAnswer={answer}
        onBack={goBack}
        onSummary={() => dispatch({ type: 'SUMMARY' })}
        onGlossary={() => setShowGlossary(true)}
        onMode={() => dispatch({ type: 'BACK', stage: 'experience' })}
        feedback={state.feedback.questions[currentQuestion.id]}
        onFeedback={(value) => dispatch({ type: 'QUESTION_FEEDBACK', questionId: currentQuestion.id, value })}
      />
      {Object.keys(state.observations).length > 0 && <Card className="guided-compact-ledger"><details><summary>Recorded evidence ({Object.keys(state.observations).length})</summary><EvidenceLedger questions={demoQuestions} observations={state.observations} conflicts={conflicts} onEdit={edit} /></details></Card>}
    </>}
    {state.stage === 'summary' && <SummaryStep questions={demoQuestions} observations={state.observations} conflicts={conflicts} profiles={compatible} scoredProfiles={scored} feedback={state.feedback.summary} context={state.context} experience={state.experience} onFeedback={(field, value) => dispatch({ type: 'SUMMARY_FEEDBACK', field, value })} onEdit={edit} onContinue={findNext(state.observations) ? continueObservation : null} onRestart={() => dispatch({ type: 'RESTART' })} onCopy={copyData} />}
    {showGlossary && <GlossaryDialog entries={demoGlossary} onClose={() => setShowGlossary(false)} />}
  </>
}
